import React, { FC, useEffect, useState } from 'react'
import { CrewAppraisal, ReviewDepartment } from '../core/_requests'
import { useAuth } from '../../auth'

type Props = {
  visible: boolean
  appraisal: CrewAppraisal | null
  isLoading?: boolean
  onClose: () => void
  onSubmitDepartmentReview: (department: ReviewDepartment, remarks: string, rating: number) => void
}

const WRAP_STYLE: React.CSSProperties = {
  background: 'rgba(0,0,0,0.5)',
  position: 'fixed',
  inset: 0,
  zIndex: 1050,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}

const DEPARTMENTS: { key: ReviewDepartment; label: string }[] = [
  { key: 'OPERATION', label: 'Operation' },
  { key: 'TECHNICAL', label: 'Technical' },
  { key: 'QHSC', label: 'QHSC' },
  { key: 'CREWING', label: 'Crewing' },
]

const getDeptReview = (appraisal: CrewAppraisal, dept: ReviewDepartment) => {
  switch (dept) {
    case 'OPERATION':
      return { remarks: appraisal.operationRemarks, rating: appraisal.operationRating, reviewedAt: appraisal.operationReviewedAt, reviewedBy: appraisal.operationReviewedByName }
    case 'TECHNICAL':
      return { remarks: appraisal.technicalRemarks, rating: appraisal.technicalRating, reviewedAt: appraisal.technicalReviewedAt, reviewedBy: appraisal.technicalReviewedByName }
    case 'QHSC':
      return { remarks: appraisal.qhscRemarks, rating: appraisal.qhscRating, reviewedAt: appraisal.qhscReviewedAt, reviewedBy: appraisal.qhscReviewedByName }
    case 'CREWING':
      return { remarks: appraisal.crewingRemarks, rating: appraisal.crewingRating, reviewedAt: appraisal.crewingReviewedAt, reviewedBy: appraisal.crewingReviewedByName }
  }
}

const CrewAppraisalReviewModal: FC<Props> = ({
  visible,
  appraisal,
  isLoading = false,
  onClose,
  onSubmitDepartmentReview,
}) => {
  const { currentUser, auth } = useAuth()
  const [activeTab, setActiveTab] = useState<ReviewDepartment>('OPERATION')
  const [remarks, setRemarks] = useState('')
  const [rating, setRating] = useState<number>(5)
  const [error, setError] = useState('')
  const prevReviewCountRef = React.useRef<number | null>(null)

  // Reset to first tab only when modal opens (visible changes to true)
  useEffect(() => {
    if (!visible || !appraisal) return
    setActiveTab('OPERATION')
    setRemarks('')
    setRating(5)
    setError('')
    prevReviewCountRef.current = appraisal.reviewCount ?? 0
  }, [visible])

  // Auto-advance to next unreviewed tab when a review is submitted
  useEffect(() => {
    if (!visible || !appraisal) return
    const currentCount = appraisal.reviewCount ?? 0
    if (prevReviewCountRef.current !== null && currentCount > prevReviewCountRef.current) {
      // A new review was just submitted — find the next unreviewed department
      const nextDept = DEPARTMENTS.find((d) => {
        const rev = getDeptReview(appraisal, d.key)
        return !rev.reviewedAt
      })
      if (nextDept) {
        setActiveTab(nextDept.key)
      }
    }
    prevReviewCountRef.current = currentCount
  }, [appraisal, visible])

  useEffect(() => {
    if (!appraisal) return
    const existing = getDeptReview(appraisal, activeTab)
    setRemarks(existing.remarks || '')
    setRating(existing.rating || 5)
    setError('')
  }, [activeTab, appraisal])

  if (!visible || !appraisal) return null

  const currentReview = getDeptReview(appraisal, activeTab)
  const isAlreadyReviewed = !!currentReview.reviewedAt
  const reviewedCount = appraisal.reviewCount ?? 0
  const allReviewed = reviewedCount >= 4
  const loggedInReviewerName = (
    (currentUser as any)?.roleEntityName ||
    currentUser?.username ||
    (auth?.userDetails as any)?.username ||
    'User'
  ).toString().trim()
  const remarksAuthorName = (currentReview.reviewedBy || loggedInReviewerName || 'User').toString().trim()
  const hasRemarks = Boolean((remarks || '').trim())

  const validate = () => {
    if (!remarks.trim()) {
      setError('Remarks are required')
      return false
    }
    if (rating < 1 || rating > 10) {
      setError('Rating must be between 1 and 10')
      return false
    }
    setError('')
    return true
  }

  const activeDeptLabel = DEPARTMENTS.find((d) => d.key === activeTab)?.label || ''

  return (
    <div style={WRAP_STYLE}>
      <div
        className='bg-white rounded shadow-lg'
        style={{ width: '60vw', maxWidth: 900, maxHeight: '90vh', overflow: 'auto' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className='d-flex justify-content-between align-items-center border-bottom px-5 py-4'>
          <h5 className='m-0 text-dark'>Review Crew Appraisal</h5>
          <button type='button' className='btn-close' onClick={onClose} disabled={isLoading} />
        </div>

        <div className='p-5'>
          {/* Appraisal info */}
          <p className='text-muted mb-4'>
            {appraisal.crewName} | {appraisal.rankName} | {appraisal.appraisalType}
          </p>

          {/* Review progress summary */}
          <div className='d-flex align-items-center gap-3 mb-5'>
            {DEPARTMENTS.map((dept) => {
              const rev = getDeptReview(appraisal, dept.key)
              const done = !!rev.reviewedAt
              return (
                <div
                  key={dept.key}
                  className={`badge py-2 px-3 ${done ? 'badge-light-success' : 'badge-light-secondary'}`}
                  style={{ fontSize: 12 }}
                >
                  {dept.label}: {done ? `${rev.rating}/10` : 'Pending'}
                </div>
              )
            })}
            <span className='text-muted ms-auto' style={{ fontSize: 13 }}>
              {reviewedCount}/4 completed
            </span>
          </div>

          {/* Department tabs */}
          <div className='d-flex gap-2 mb-4'>
            {DEPARTMENTS.map((dept) => {
              const rev = getDeptReview(appraisal, dept.key)
              const done = !!rev.reviewedAt
              const isActive = activeTab === dept.key
              return (
                <button
                  key={dept.key}
                  type='button'
                  className={`btn btn-sm ${isActive ? 'btn_primary' : 'btn-light'}`}
                  onClick={() => setActiveTab(dept.key)}
                  disabled={isLoading}
                  style={{ minWidth: 100 }}
                >
                  {done && <span className='me-1'>&#10003;</span>}
                  {dept.label}
                </button>
              )
            })}
          </div>

          {/* Already reviewed — read-only display */}
          {isAlreadyReviewed && (
            <div className='border rounded p-4 bg-light mb-4'>
              <div className='d-flex justify-content-between align-items-center mb-3'>
                <span className='fw-semibold text-dark'>{activeDeptLabel} — Submitted Review</span>
                <span className='badge badge-light-success'>Completed</span>
              </div>
              <div className='row g-3'>
                <div className='col-md-2'>
                  <label className='form-label fw-semibold text-dark' style={{ fontSize: 13 }}>Rating</label>
                  <div className='fw-bold text-dark fs-4'>{currentReview.rating}/10</div>
                </div>
                <div className='col-md-10'>
                  <label className='form-label fw-semibold text-dark' style={{ fontSize: 13 }}>Remarks</label>
                  <div className='text-dark' style={{ whiteSpace: 'pre-wrap' }}>
                    {currentReview.remarks || 'No remarks'}
                  </div>
                </div>
              </div>
              <div className='text-muted mt-2' style={{ fontSize: 12 }}>
                Reviewed by {currentReview.reviewedBy || 'N/A'}
                {currentReview.reviewedAt && ` on ${new Date(currentReview.reviewedAt).toLocaleString()}`}
              </div>
            </div>
          )}

          {/* Review form — shown for new or update */}
          <div>
            {isAlreadyReviewed && (
              <p className='text-muted small mb-3'>You can update this review by modifying the fields below and re-submitting.</p>
            )}

            <div className='row g-4'>
              <div className='col-md-3'>
                <label className='form-label fw-semibold text-dark'>
                  Rating (out of 10) <span className='text-danger'>*</span>
                </label>
                <input
                  type='number'
                  className='form-control text-dark bg-white'
                  min={1}
                  max={10}
                  value={rating}
                  onChange={(e) => {
                    setRating(Number(e.target.value))
                    if (error) setError('')
                  }}
                  disabled={isLoading}
                  placeholder='1 - 10'
                />
              </div>
              <div className='col-md-9'>
                <label className='form-label fw-semibold text-dark'>
                  {activeDeptLabel} Remarks <span className='text-danger'>*</span>
                </label>
                <textarea
                  className='form-control text-dark bg-white'
                  rows={4}
                  value={remarks}
                  onChange={(e) => {
                    setRemarks(e.target.value)
                    if (error) setError('')
                  }}
                  placeholder={`Enter ${activeDeptLabel.toLowerCase()} review remarks...`}
                  disabled={isLoading}
                />
                {(isAlreadyReviewed || hasRemarks) && (
                  <div className='text-muted small mt-2'>{remarksAuthorName} filled the remarks</div>
                )}
              </div>
            </div>
            {error && <div className='text-danger small mt-2'>{error}</div>}
          </div>
        </div>

        {/* Footer */}
        <div className='border-top d-flex justify-content-between align-items-center px-5 py-4'>
          <div className='text-muted small'>
            {allReviewed
              ? 'All 4 department reviews are complete. Appraisal is approved.'
              : `${4 - reviewedCount} review(s) remaining before approval.`}
          </div>
          <div className='d-flex gap-2'>
            <button type='button' className='btn btn-light' onClick={onClose} disabled={isLoading}>
              Cancel
            </button>
            <button
              type='button'
              className='btn btn_primary'
              disabled={isLoading}
              onClick={() => {
                if (!validate()) return
                onSubmitDepartmentReview(activeTab, remarks, rating)
              }}
            >
              {isLoading
                ? 'Submitting...'
                : isAlreadyReviewed
                  ? `Update ${activeDeptLabel} Review`
                  : `Submit ${activeDeptLabel} Review`}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CrewAppraisalReviewModal
